import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from './Modal';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });

  const activeTickets = tickets.filter(t => t.status !== 'closed');
  const closedTickets = tickets.filter(t => t.status === 'closed');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      alert('Error loading tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/tickets`, formData);
      setShowCreateModal(false);
      setFormData({ subject: '', message: '', priority: 'medium' });
      await fetchTickets();
      // Ticket created successfully - no alert needed
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Error creating ticket: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }

    try {
      await axios.put(`${API_BASE}/tickets/${ticketId}/close`);
      await fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(null);
      }
      alert('Ticket closed successfully!');
    } catch (error) {
      console.error('Error closing ticket:', error);
      alert('Error closing ticket: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return '#ffd700';
      case 'in_progress':
        return '#4a9eff';
      case 'closed':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ff6b6b';
      case 'medium':
        return '#ffd700';
      case 'low':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  if (loading) {
    return (
      <div className="tickets-page">
        <div className="loading-screen">
          <p>Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        <div>
          <h1>Support Tickets</h1>
          <p className="tickets-subtitle">Create and manage your support requests</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowCreateModal(true)}
        >
          + New Ticket
        </button>
      </div>

      {activeTickets.length === 0 && closedTickets.length === 0 ? (
        <div className="tickets-empty">
          <p>No tickets yet. Create your first support ticket!</p>
        </div>
      ) : (
        <>
          {/* Active Tickets */}
          {activeTickets.length > 0 && (
            <div className="tickets-section">
              <h2>Active Tickets ({activeTickets.length})</h2>
              <div className="tickets-list">
                {activeTickets.map((ticket) => (
                  <div 
                    key={ticket.id} 
                    className="ticket-card"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="ticket-card-header">
                      <h3>{ticket.subject}</h3>
                      <div className="ticket-badges">
                        <span 
                          className="ticket-badge ticket-status"
                          style={{ backgroundColor: getStatusColor(ticket.status), color: '#1a1a1a' }}
                        >
                          {ticket.status.replace('_', ' ')}
                        </span>
                        <span 
                          className="ticket-badge ticket-priority"
                          style={{ backgroundColor: getPriorityColor(ticket.priority), color: '#1a1a1a' }}
                        >
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                    <p className="ticket-message-preview">{ticket.message}</p>
                    <div className="ticket-card-footer">
                      <span className="ticket-date">
                        Created: {new Date(ticket.created_at).toLocaleString()}
                      </span>
                      {ticket.updated_at !== ticket.created_at && (
                        <span className="ticket-date">
                          Updated: {new Date(ticket.updated_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Closed/Archived Tickets */}
          {closedTickets.length > 0 && (
            <div className="tickets-section tickets-archived">
              <div className="tickets-archived-header">
                <h2>Closed Tickets ({closedTickets.length})</h2>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowArchived(!showArchived)}
                  style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                >
                  {showArchived ? '▼ Hide' : '▶ Show'} Archived
                </button>
              </div>
              {showArchived && (
                <div className="tickets-list tickets-list-archived">
                  {closedTickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className="ticket-card ticket-card-archived"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="ticket-card-header">
                        <h3>{ticket.subject}</h3>
                        <div className="ticket-badges">
                          <span 
                            className="ticket-badge ticket-status"
                            style={{ backgroundColor: getStatusColor(ticket.status), color: '#1a1a1a' }}
                          >
                            {ticket.status.replace('_', ' ')}
                          </span>
                          <span 
                            className="ticket-badge ticket-priority"
                            style={{ backgroundColor: getPriorityColor(ticket.priority), color: '#1a1a1a' }}
                          >
                            {ticket.priority}
                          </span>
                        </div>
                      </div>
                      <p className="ticket-message-preview">{ticket.message}</p>
                      <div className="ticket-card-footer">
                        <span className="ticket-date">
                          Created: {new Date(ticket.created_at).toLocaleString()}
                        </span>
                        <span className="ticket-date">
                          Closed: {new Date(ticket.updated_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Ticket Modal */}
      <Modal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        title="Create New Ticket"
      >
        <form onSubmit={handleCreateTicket}>
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="form-input"
              required
              placeholder="Brief description of your issue"
            />
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="form-input"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="form-input form-textarea"
              rows="6"
              required
              placeholder="Describe your issue in detail..."
            />
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Ticket
            </button>
          </div>
        </form>
      </Modal>

      {/* Ticket Details Modal */}
      <Modal 
        isOpen={!!selectedTicket} 
        onClose={() => setSelectedTicket(null)}
        title={`Ticket #${selectedTicket?.id}: ${selectedTicket?.subject}`}
      >
        {selectedTicket && (
          <div className="ticket-details">
            <div className="ticket-details-header">
              <div className="ticket-badges">
                <span 
                  className="ticket-badge ticket-status"
                  style={{ backgroundColor: getStatusColor(selectedTicket.status), color: '#1a1a1a' }}
                >
                  {selectedTicket.status.replace('_', ' ')}
                </span>
                <span 
                  className="ticket-badge ticket-priority"
                  style={{ backgroundColor: getPriorityColor(selectedTicket.priority), color: '#1a1a1a' }}
                >
                  {selectedTicket.priority}
                </span>
              </div>
            </div>

            <div className="ticket-details-body">
              <div className="ticket-section">
                <h4>Your Message</h4>
                <p className="ticket-message">{selectedTicket.message}</p>
                <small className="ticket-meta">
                  Created: {new Date(selectedTicket.created_at).toLocaleString()}
                </small>
              </div>

              {selectedTicket.admin_response && (
                <div className="ticket-section ticket-admin-response">
                  <h4>Admin Response</h4>
                  <p className="ticket-message" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.admin_response}
                  </p>
                  <small className="ticket-meta">
                    Last updated: {new Date(selectedTicket.updated_at).toLocaleString()}
                  </small>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setSelectedTicket(null)}
              >
                Close
              </button>
              {selectedTicket.status !== 'closed' && (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleCloseTicket(selectedTicket.id)}
                  style={{ background: '#ff6b6b' }}
                >
                  Close Ticket
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Tickets;

